/* import __COLOCATED_TEMPLATE__ from './multilingual.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type TeammateAiTranslationSettings from 'embercom/services/teammate-ai-translation-settings';
import { NO_ACCESS } from 'embercom/lib/settings/copilot-access-editor/constants';

interface Args {
  model: {
    messengerSettings: any;
    generalSettings: any;
    languages: any;
    adminTeammateAppSettings: any;
  };
}

interface Signature {
  Args: Args;
}

export default class Multilingual extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: $TSFixMe;
  @service declare appService: AppService;
  @service declare teammateAiTranslationSettings: TeammateAiTranslationSettings;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  get title() {
    return this.intl.t('new-settings.submenu.personal.multilingual');
  }

  get canAccessLanguageSettings() {
    return this.appService.app?.currentAdmin?.copilotAccessType !== NO_ACCESS;
  }

  get app() {
    return this.appService.app;
  }

  get messengerSettings() {
    return this.args.model?.messengerSettings;
  }

  get generalSettings() {
    return this.args.model?.generalSettings;
  }

  get adminTeammateAppSettings() {
    return this.args.model?.adminTeammateAppSettings;
  }

  get languageSettings() {
    // Use languages directly from model instead of through messengerSettings
    return this.args.model?.languages;
  }

  get supportedLocales() {
    let locales = this.languageSettings?.supportedLocales;
    if (!locales) {
      return [];
    }
    // Handle both Ember Data fragment arrays and plain arrays
    return Array.isArray(locales) ? locales : locales.toArray?.() || [];
  }

  get appLocales() {
    return this.generalSettings?.app_locales || [];
  }

  get defaultLocaleObject() {
    if (!this.supportedLocales || !Array.isArray(this.supportedLocales)) {
      return null;
    }
    return this.supportedLocales.find(
      (locale: any) => locale.localeId === this.languageSettings?.defaultLocale,
    );
  }

  get translationsProductConfusionImprovementsEnabled() {
    if (this.app?.canUseFeature('team-inbox-translations-simplified-settings-opt-out')) {
      return false;
    }
    return this.app?.canUseFeature('team-inbox-translations-product-confusion-improvements');
  }

  get defaultLocaleName() {
    return this.defaultLocaleObject?.name ?? this.app?.locale ?? '';
  }

  get preferredTranslationLanguage() {
    return this.adminTeammateAppSettings?.preferred_locale;
  }

  get validatedPreferredTranslationLanguage() {
    let selectedValue = this.preferredTranslationLanguage;
    if (selectedValue) {
      // Ensure the selected value exists in the available options
      let hasMatchingOption = this.preferredLanguageOptions.some(
        (option) => option.value === selectedValue,
      );
      if (hasMatchingOption) {
        return selectedValue;
      }
    }

    if (this.translationsProductConfusionImprovementsEnabled) {
      // Default to workspace locale — this is what the backend already uses as the
      // fallback when preferred_locale is null, so we're making the implicit explicit.
      return this.app?.locale ?? null;
    }

    return null;
  }

  get preferredLanguageOptions() {
    // Safety check - return empty array if supportedLocales isn't loaded yet
    if (
      !this.supportedLocales ||
      !Array.isArray(this.supportedLocales) ||
      this.supportedLocales.length === 0
    ) {
      return [];
    }

    // Preferred Languages = app.locale + app_locales from general-app-settings
    // These should be available as options for the preferred translation language selector
    let preferredLocaleIds = new Set<string>();

    // Add app.locale (workspace default)
    let appLocale = this.app?.locale;
    if (appLocale) {
      preferredLocaleIds.add(appLocale);
    }

    // Add app_locales from general settings
    let appLocales = this.appLocales || [];
    appLocales.forEach((localeId: string) => {
      preferredLocaleIds.add(localeId);
    });

    // Map to the format expected by IcSelect
    return this.supportedLocales
      .filter((locale: any) => locale && locale.localeId && preferredLocaleIds.has(locale.localeId))
      .map((locale: any) => ({
        value: locale.localeId,
        text: locale.name,
      }));
  }

  get excludedLanguages() {
    return this.teammateAiTranslationSettings.excludedLanguages || [];
  }

  get excludedLanguageObjects() {
    if (!this.supportedLocales || !Array.isArray(this.supportedLocales)) {
      return [];
    }
    return this.supportedLocales.filter((lang: any) =>
      this.excludedLanguages.includes(lang.localeId),
    );
  }

  get availableLanguageOptions() {
    if (
      !this.supportedLocales ||
      !Array.isArray(this.supportedLocales) ||
      this.supportedLocales.length === 0
    ) {
      return [];
    }

    // Languages you know = all supportedLocales minus (excludedLanguages + preferred languages)
    let excludedLocaleIds = new Set<string>(this.excludedLanguages);

    // Also exclude app.locale (workspace default)
    let appLocale = this.app?.locale;
    if (appLocale) {
      excludedLocaleIds.add(appLocale);
    }

    // Also exclude app_locales from general settings
    let appLocales = this.appLocales || [];
    appLocales.forEach((localeId: string) => {
      excludedLocaleIds.add(localeId);
    });

    return this.supportedLocales
      .filter((lang: any) => {
        return lang && lang.localeId && !excludedLocaleIds.has(lang.localeId);
      })
      .map((lang: any) => ({
        value: lang.localeId,
        text: lang.name,
        locale: lang,
      }));
  }

  @action
  async setPreferredTranslationLanguage(localeId: string) {
    if (!this.adminTeammateAppSettings) {
      return;
    }

    try {
      this.adminTeammateAppSettings.preferred_locale = localeId;
      await this.adminTeammateAppSettings.save();

      this.intercomEventService.trackAnalyticsEvent({
        action: 'changed',
        object: 'preferred_translation_language',
        locale_id: localeId,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'new-settings.personal.preferences.preferred-translation-language.update-success',
        ),
      );
    } catch (error) {
      console.error('Failed to save preferred translation language:', error);
      this.notificationsService.notifyError(
        this.intl.t(
          'new-settings.personal.preferences.preferred-translation-language.update-error',
        ),
      );
    }
  }

  @action
  addExcludedLanguage(languageOption: any) {
    // Extract localeId from the languageOption object
    let localeId = languageOption.value || languageOption.localeId || languageOption;
    let newExcludedLanguages = [...this.excludedLanguages, localeId];

    this.intercomEventService.trackAnalyticsEvent({
      action: 'added',
      object: 'excluded_language',
      locale_id: localeId,
      excluded_language_count: newExcludedLanguages.length,
    });

    return taskFor(this.teammateAiTranslationSettings.updateExcludedLanguages).perform(
      newExcludedLanguages,
    );
  }

  @action
  removeExcludedLanguage(localeId: string) {
    let newExcludedLanguages = this.excludedLanguages.filter((code) => code !== localeId);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'removed',
      object: 'excluded_language',
      locale_id: localeId,
      excluded_language_count: newExcludedLanguages.length,
    });

    return taskFor(this.teammateAiTranslationSettings.updateExcludedLanguages).perform(
      newExcludedLanguages,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Personal::Multilingual': typeof Multilingual;
    'new-settings/personal/multilingual': typeof Multilingual;
  }
}
